import { useState, useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Search, SlidersHorizontal, X, Wifi, Snowflake, WashingMachine, Tv, Waves, Shirt, Flame, Coffee, Dumbbell, Car, BedDouble, Zap, Baby, Crown, UtensilsCrossed, CircleAlert, ShieldCheck, Star, Home, Hotel, DoorOpen, Sailboat, Minus, Plus, Check, Container, Music, TreePalm, Landmark, ChevronDown, Calendar, Users, ChevronRight, MapPin } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { cn } from "@/lib/utils";
import { ApartmentIcon } from "@/components/icons/PropertyTypeIcons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import {
  Dialog,
  DialogContent,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetOverlay,
} from "@/components/ui/sheet";
import { ScrollArea } from "@/components/ui/scroll-area";
import { useIsMobile } from "@/hooks/use-mobile";
import { motion, AnimatePresence } from "framer-motion";

import { tAuto } from "@/lib/tAuto";
export interface FilterValues {
  search: string;
  province: string;
  propertyType: string;
  priceRange: [number, number];
  bedrooms: number;
  bathrooms: number;
  beds: number;
  amenities: string[];
  features: string[];
  safety: string[];
  highlights: string[];
  propertyCategory: string[];
  hostLanguage: string[];
  accommodationType: string;
}

interface PropertyFiltersProps {
  filters: FilterValues;
  onFiltersChange: (filters: FilterValues) => void;
  totalResults: number;
  variant?: "guest" | "host";
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
}

const ACCOMMODATION_TYPES = [
  { value: "", label: "Qualquer tipo", desc: "Todos os tipos" },
  { value: "quarto", label: "Quarto", desc: "Um quarto privado" },
  { value: "espaco_inteiro", label: "Espaço inteiro", desc: "Todo o espaço para si" },
];

const AMENITIES = [
  { id: "wifi", label: "Wi-Fi", icon: Wifi },
  { id: "ac", label: "Ar-condicionado", icon: Snowflake },
  { id: "washer", label: "Máquina de lavar", icon: WashingMachine },
  { id: "tv", label: "TV", icon: Tv },
  { id: "pool", label: "Piscina", icon: Waves },
  { id: "dryer", label: "Secadora", icon: Shirt },
];

const FEATURES = [
  { id: "jacuzzi", label: "Hidromassagem", icon: Waves },
  { id: "parking", label: "Garagem", icon: Car },
  { id: "ev_charger", label: "Carregador EV", icon: Zap },
  { id: "crib", label: "Berço", icon: Baby },
  { id: "king_bed", label: "Cama king", icon: BedDouble },
  { id: "gym", label: "Academia", icon: Dumbbell },
  { id: "bbq", label: "Churrasqueira", icon: Flame },
  { id: "breakfast", label: "Café da manhã", icon: Coffee },
  { id: "fireplace", label: "Lareira interna", icon: Flame },
  { id: "smoking", label: "Permitido fumar", icon: CircleAlert },
];

const SAFETY = [
  { id: "smoke_detector", label: "Detector de fumaça", icon: ShieldCheck },
  { id: "co_alarm", label: "Alarme de monóxido de carbono", icon: ShieldCheck },
];

const HIGHLIGHTS = [
  { id: "top_rated", label: "Mais preferidas pelos hóspedes", icon: Star },
  { id: "luxury", label: "Acomodações de luxo", icon: Crown },
];

const PROPERTY_CATEGORIES = [
  { id: "hotel", label: "Hotel", icon: Hotel, color: "from-blue-400 to-blue-600" },
  { id: "hospedaria", label: "Hospedaria", icon: Home, color: "from-emerald-400 to-emerald-600" },
  { id: "barco", label: "Barcos", icon: Sailboat, color: "from-cyan-400 to-blue-500" },
  { id: "casa", label: "Casa", icon: Home, color: "from-amber-400 to-orange-500" },
  { id: "apartamento", label: "Apartamento", icon: ApartmentIcon, color: "from-primary to-primary" },
  { id: "casa_hospedes", label: "Casa de hóspedes", icon: DoorOpen, color: "from-rose-400 to-pink-500" },
  { id: "condominio", label: "Condomínio", icon: Landmark, color: "from-primary to-primary" },
  { id: "contentor", label: "Contentor", icon: Container, color: "from-slate-400 to-slate-600" },
  { id: "resort", label: "Resort", icon: TreePalm, color: "from-teal-400 to-teal-600" },
];

// Host languages removed — not relevant for Angola market

/* ───────── Stepper Counter ───────── */
const StepperCounter = ({ value, onChange, label }: { value: number; onChange: (v: number) => void; label: string }) => (
  <div className="flex items-center justify-between py-3">
    <span className="text-[15px] font-medium text-foreground">{label}</span>
    <div className="flex items-center gap-3">
      <button
        onClick={() => onChange(Math.max(0, value - 1))}
        disabled={value === 0}
        className={cn(
          "w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-200",
          value === 0
            ? "border-border/40 text-muted-foreground/40 cursor-not-allowed"
            : "border-foreground/30 text-foreground hover:border-foreground hover:bg-foreground/5 active:scale-95"
        )}
      >
        <Minus className="w-3.5 h-3.5" />
      </button>
      <span className={cn(
        "w-8 text-center text-[15px] font-medium tabular-nums",
        value === 0 ? "text-muted-foreground" : "text-foreground"
      )}>
        {value === 0 ? "–" : value === 8 ? "8+" : value}
      </span>
      <button
        onClick={() => onChange(Math.min(8, value + 1))}
        disabled={value === 8}
        className={cn(
          "w-9 h-9 rounded-full border-2 flex items-center justify-center transition-all duration-200",
          value === 8
            ? "border-border/40 text-muted-foreground/40 cursor-not-allowed"
            : "border-foreground/30 text-foreground hover:border-foreground hover:bg-foreground/5 active:scale-95"
        )}
      >
        <Plus className="w-3.5 h-3.5" />
      </button>
    </div>
  </div>
);

/* ───────── Pill Selectable ───────── */
const PillItem = ({ label, icon: Icon, selected, onClick }: {
  label: string; icon?: any; selected: boolean; onClick: () => void;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "flex items-center gap-2 px-3 py-2 rounded-xl border-2 text-xs font-medium transition-all duration-200 active:scale-[0.97]",
      selected
        ? "border-primary bg-primary/8 text-foreground shadow-sm"
        : "border-border/50 text-muted-foreground hover:border-foreground/25 hover:text-foreground hover:bg-muted/30"
    )}
  >
    {Icon && (
      <div className={cn(
        "w-6 h-6 rounded-lg flex items-center justify-center transition-colors shrink-0",
        selected ? "bg-primary/15" : "bg-muted/60"
      )}>
        <Icon className={cn("w-3.5 h-3.5", selected ? "text-primary" : "text-muted-foreground")} />
      </div>
    )}
    <span>{label}</span>
    {selected && (
      <Check className="w-3.5 h-3.5 text-primary ml-auto" />
    )}
  </button>
);

/* ───────── Property Type Pill ───────── */
const PropertyTypePill = ({ label, icon: Icon, selected, onClick, color }: {
  label: string; icon: any; selected: boolean; onClick: () => void; color?: string;
}) => (
  <button
    onClick={onClick}
    className={cn(
      "inline-flex items-center gap-2 px-3 py-2 rounded-xl border-2 transition-all duration-200 active:scale-[0.97] whitespace-nowrap shadow-sm",
      selected
        ? "border-[#E8513D] bg-gradient-to-r from-[#E8513D]/10 to-[#D94F30]/5 shadow-md shadow-[#E8513D]/20"
        : "border-gray-200 hover:border-[#E8513D]/40 hover:bg-[#E8513D]/5 bg-white"
    )}
  >
    <div className={cn(
      "w-6 h-6 rounded-lg flex items-center justify-center shadow-sm transition-all shrink-0",
      selected ? `bg-gradient-to-br ${color || 'from-[#E8513D] to-[#D94F30]'}` : `bg-gradient-to-br ${color || 'from-gray-100 to-gray-200'}`
    )}>
      <Icon className={cn("w-3.5 h-3.5", selected ? "text-white" : "text-white")} strokeWidth={2.5} />
    </div>
    <span className={cn("text-xs font-bold", selected ? "text-[#D94F30]" : "text-gray-700")}>{label}</span>
    {selected && (
      <div className="w-5 h-5 rounded-full bg-[#E8513D] flex items-center justify-center ml-1">
        <Check className="w-3 h-3 text-white" strokeWidth={3} />
      </div>
    )}
  </button>
);

/* ───────── Section Divider ───────── */
const SectionDivider = () => (
  <div className="w-full h-[1px] bg-gradient-to-r from-transparent via-[#DCD6CC]/80 dark:via-[#DCD6CC]/30 to-transparent my-1" />
);

/* ───────── Section Header ───────── */
const SectionHeader = ({ title, subtitle }: { title: string; subtitle?: string }) => (
  <div className="mb-3">
    <h3 className="font-display text-base font-semibold text-foreground tracking-tight">{title}</h3>
    {subtitle && <p className="text-xs text-muted-foreground mt-1 leading-snug">{subtitle}</p>}
  </div>
);

/* ───────── Main Component ───────── */
const PropertyFilters = ({ filters, onFiltersChange, totalResults, variant = "guest", isOpen: externalIsOpen, onOpenChange }: PropertyFiltersProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (value: boolean) => {
    if (externalIsOpen === undefined) {
      setInternalIsOpen(value);
    }
    onOpenChange?.(value);
  };

  // Esconde o bottom tab bar quando o modal de filtros estiver aberto (mobile/tablet/native)
  // e informa o store global para o widget da Bera IA se ocultar.
  useEffect(() => {
    import("@/lib/kycUiState").then(({ kycUiStore }) => {
      kycUiStore.setFiltersOpen(isOpen);
    });
    if (isMobile) {
      window.dispatchEvent(
        new CustomEvent("berastays:bottom-tabs", { detail: { hidden: isOpen } })
      );
    }
    return () => {
      import("@/lib/kycUiState").then(({ kycUiStore }) => {
        kycUiStore.setFiltersOpen(false);
      });
      if (isMobile) {
        window.dispatchEvent(
          new CustomEvent("berastays:bottom-tabs", { detail: { hidden: false } })
        );
      }
    };
  }, [isMobile, isOpen]);
  const [tempFilters, setTempFilters] = useState<FilterValues>(filters);
  const [showAllCategories, setShowAllCategories] = useState(false);

  const activeFiltersCount = [
    filters.accommodationType,
    filters.priceRange[0] > 0 || filters.priceRange[1] < 500000,
    filters.bedrooms > 0,
    filters.bathrooms > 0,
    filters.beds > 0,
    filters.amenities.length > 0,
    filters.features.length > 0,
    filters.safety.length > 0,
    filters.highlights.length > 0,
    filters.propertyCategory.length > 0,
    filters.hostLanguage.length > 0,
  ].filter(Boolean).length;

  const handleOpen = () => {
    setTempFilters(filters);
    setIsOpen(true);
  };

  const handleApplyFilters = () => {
    onFiltersChange(tempFilters);
    setIsOpen(false);
  };

  const handleClearFilters = () => {
    const cleared: FilterValues = {
      search: filters.search,
      province: "",
      propertyType: "",
      priceRange: [0, 500000],
      bedrooms: 0,
      bathrooms: 0,
      beds: 0,
      amenities: [],
      features: [],
      safety: [],
      highlights: [],
      propertyCategory: [],
      hostLanguage: [],
      accommodationType: "",
    };
    setTempFilters(cleared);
    onFiltersChange(cleared);
    setIsOpen(false);
  };

  const toggleList = (key: keyof FilterValues, id: string) => {
    setTempFilters(prev => {
      const list = prev[key] as string[];
      return {
        ...prev,
        [key]: list.includes(id) ? list.filter(a => a !== id) : [...list, id],
      };
    });
  };

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('pt-AO').format(price) + ' Kz';
  };

  return (
    <div className="space-y-3">

      {/* Filtro: Sheet lateral no desktop sem overlay (página visível), Dialog modal no mobile */}
      {!isMobile ? (
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetOverlay className="bg-black/20 backdrop-blur-none" />
          <SheetContent side="right" className="w-full sm:max-w-md md:max-w-lg p-0 gap-0 flex flex-col border-l border-border/40 shadow-2xl bg-background">
            {/* Header Desktop */}
            <div className="relative px-6 py-5 border-b border-border/40 flex-shrink-0 bg-gradient-to-b from-background to-muted/20">
              <h2 className="font-display text-xl font-bold tracking-tight">{t('search.filters')}</h2>
              <p className="text-xs text-muted-foreground mt-1">{t('search.refineSearch')}</p>
            </div>

            {/* Scrollable Content */}
            <ScrollArea className="flex-1 overflow-y-auto">
              <div className="px-5 py-5 space-y-5 pb-32">

                {/* 1. Tipo de acomodação */}
                <section>
                  <SectionHeader title={t("explore.accommodationType") || "Tipo de acomodação"} />
                  <div className="grid grid-cols-3 gap-2.5">
                    {ACCOMMODATION_TYPES.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setTempFilters(prev => ({ ...prev, accommodationType: type.value }))}
                        className={cn(
                          "flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border-2 transition-all duration-200 active:scale-[0.97]",
                          tempFilters.accommodationType === type.value
                            ? "border-primary bg-primary/8 shadow-sm"
                            : "border-border/50 hover:border-foreground/25"
                        )}
                      >
                        <span className="text-sm font-semibold text-foreground">{type.label}</span>
                        <span className="text-[11px] text-muted-foreground">{type.desc}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <SectionDivider />

                {/* 2. Faixa de preço */}
                <section>
                  <SectionHeader title={t("explore.priceRange") || "Faixa de preço"} subtitle="Preço por noite incluindo taxas e impostos" />
                  <div className="px-1 mb-4">
                    <Slider
                      value={tempFilters.priceRange}
                      onValueChange={(value) => setTempFilters(prev => ({ ...prev, priceRange: value as [number, number] }))}
                      min={0}
                      max={500000}
                      step={5000}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">{t('search.min')}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={tempFilters.priceRange[0]}
                          onChange={(e) => setTempFilters(prev => ({ ...prev, priceRange: [Number(e.target.value), prev.priceRange[1]] }))}
                          className="h-12 rounded-xl pl-3 pr-10 bg-muted/30 border-border/40 focus:bg-card"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">Kz</span>
                      </div>
                    </div>
                    <div className="flex items-end pb-3.5 text-muted-foreground/40 font-light text-lg">–</div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">{t('search.max')}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={tempFilters.priceRange[1]}
                          onChange={(e) => setTempFilters(prev => ({ ...prev, priceRange: [prev.priceRange[0], Number(e.target.value)] }))}
                          className="h-12 rounded-xl pl-3 pr-10 bg-muted/30 border-border/40 focus:bg-card"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">Kz</span>
                      </div>
                    </div>
                  </div>
                </section>

                <SectionDivider />

                {/* 3. Quartos e camas */}
                <section>
                  <SectionHeader title={t("explore.roomsAndBeds") || "Quartos e camas"} />
                  <div className="space-y-1 divide-y divide-border/30">
                    <StepperCounter label={t("explore.bedrooms") || "Quartos"} value={tempFilters.bedrooms} onChange={(v) => setTempFilters(prev => ({ ...prev, bedrooms: v }))} />
                    <StepperCounter label={t("explore.beds") || "Camas"} value={tempFilters.beds} onChange={(v) => setTempFilters(prev => ({ ...prev, beds: v }))} />
                    <StepperCounter label={t("explore.bathrooms") || "WC"} value={tempFilters.bathrooms} onChange={(v) => setTempFilters(prev => ({ ...prev, bathrooms: v }))} />
                  </div>
                </section>

                <SectionDivider />

                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="amenities" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{t('search.amenities')}</span>
                        {tempFilters.amenities.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.amenities.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        {AMENITIES.map((item) => (
                          <PillItem key={item.id} label={item.label} icon={item.icon} selected={tempFilters.amenities.includes(item.id)} onClick={() => toggleList("amenities", item.id)} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="features" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{t('search.features')}</span>
                        {tempFilters.features.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.features.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        {FEATURES.map((item) => (
                          <PillItem key={item.id} label={item.label} icon={item.icon} selected={tempFilters.features.includes(item.id)} onClick={() => toggleList("features", item.id)} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="safety" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{tAuto("auto.components_explore_propertyfilters.1", "Segurança")}</span>
                        {tempFilters.safety.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.safety.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-1 gap-2">
                        {SAFETY.map((item) => (
                          <PillItem key={item.id} label={item.label} icon={item.icon} selected={tempFilters.safety.includes(item.id)} onClick={() => toggleList("safety", item.id)} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="highlights" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">Em destaque</span>
                        {tempFilters.highlights.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.highlights.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-1 gap-2">
                        {HIGHLIGHTS.map((item) => (
                          <PillItem key={item.id} label={item.label} icon={item.icon} selected={tempFilters.highlights.includes(item.id)} onClick={() => toggleList("highlights", item.id)} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="categories" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{tAuto("auto.components_explore_propertyfilters.2", "Tipo de propriedade")}</span>
                        {tempFilters.propertyCategory.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.propertyCategory.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="flex flex-wrap gap-2">
                        {PROPERTY_CATEGORIES.map((cat) => (
                          <PropertyTypePill key={cat.id} label={cat.label} icon={cat.icon} color={cat.color} selected={tempFilters.propertyCategory.includes(cat.id)} onClick={() => toggleList("propertyCategory", cat.id)} />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

              </div>
            </ScrollArea>

            {/* Footer Desktop - Sticky */}
            <div className="px-6 py-4 border-t border-border/40 flex items-center justify-between bg-card flex-shrink-0">
              <button
                onClick={handleClearFilters}
                className="text-sm font-semibold text-foreground underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground transition-colors"
              >
                {t('search.clearAll')}
              </button>
              <Button
                onClick={handleApplyFilters}
                size="lg"
                className="gradient-primary text-primary-foreground rounded-xl px-8 h-12 text-[15px] font-bold shadow-md hover:shadow-lg hover:opacity-95 transition-all active:scale-[0.98]"
              >
                {totalResults === 1 ? t('search.showResult') : t('search.showResults', { count: totalResults })}
              </Button>
            </div>
          </SheetContent>
        </Sheet>
      ) : (
        <Dialog open={isOpen} onOpenChange={setIsOpen}>
          <DialogContent className="p-0 gap-0 rounded-3xl overflow-hidden flex flex-col border border-border/60 shadow-2xl bg-background" style={{ width: 'calc(100vw - 24px)', maxWidth: '640px', maxHeight: 'calc(100dvh - 80px)', margin: '0 auto', top: '50%', transform: 'translateX(-50%) translateY(-50%)' }}>
            {/* Header */}
            <div className="relative px-6 py-5 pr-14 border-b border-border/40 flex-shrink-0">
              <h2 className="text-center font-display text-lg font-bold tracking-tight">{t('search.filters')}</h2>
            </div>

            {/* Scrollable Content */}
            <ScrollArea className="flex-1 overflow-y-auto">
              <div className="px-5 py-5 space-y-5" style={{ paddingBottom: "max(2.75rem, calc(env(safe-area-inset-bottom, 0px) + 2.25rem))" }}>

                {/* 1. Tipo de acomodação */}
                <section>
                  <SectionHeader title={t("explore.accommodationType") || "Tipo de acomodação"} />
                  <div className="grid grid-cols-3 gap-2.5">
                    {ACCOMMODATION_TYPES.map((type) => (
                      <button
                        key={type.value}
                        onClick={() => setTempFilters(prev => ({ ...prev, accommodationType: type.value }))}
                        className={cn(
                          "flex flex-col items-center gap-1 py-4 px-3 rounded-2xl border-2 transition-all duration-200 active:scale-[0.97]",
                          tempFilters.accommodationType === type.value
                            ? "border-primary bg-primary/8 shadow-sm"
                            : "border-border/50 hover:border-foreground/25"
                        )}
                      >
                        <span className={cn(
                          "text-sm font-semibold",
                          tempFilters.accommodationType === type.value ? "text-foreground" : "text-foreground"
                        )}>{type.label}</span>
                        <span className="text-[11px] text-muted-foreground">{type.desc}</span>
                      </button>
                    ))}
                  </div>
                </section>

                <SectionDivider />

                {/* 2. Faixa de preço */}
                <section>
                  <SectionHeader title={t("explore.priceRange") || "Faixa de preço"} subtitle="Preço por noite incluindo taxas e impostos" />
                  <div className="px-1 mb-4">
                    <Slider
                      value={tempFilters.priceRange}
                      onValueChange={(value) => setTempFilters(prev => ({ ...prev, priceRange: value as [number, number] }))}
                      min={0}
                      max={500000}
                      step={5000}
                      className="w-full"
                    />
                  </div>
                  <div className="flex gap-4">
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">{t('search.min')}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={tempFilters.priceRange[0]}
                          onChange={(e) => setTempFilters(prev => ({ ...prev, priceRange: [Number(e.target.value), prev.priceRange[1]] }))}
                          className="h-12 rounded-xl pl-3 pr-10 bg-muted/30 border-border/40 focus:bg-card"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">Kz</span>
                      </div>
                    </div>
                    <div className="flex items-end pb-3.5 text-muted-foreground/40 font-light text-lg">–</div>
                    <div className="flex-1">
                      <label className="text-xs font-medium text-muted-foreground mb-2 block">{t('search.max')}</label>
                      <div className="relative">
                        <Input
                          type="number"
                          value={tempFilters.priceRange[1]}
                          onChange={(e) => setTempFilters(prev => ({ ...prev, priceRange: [prev.priceRange[0], Number(e.target.value)] }))}
                          className="h-12 rounded-xl pl-3 pr-10 bg-muted/30 border-border/40 focus:bg-card"
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs font-medium text-muted-foreground">Kz</span>
                      </div>
                    </div>
                  </div>
                </section>

                <SectionDivider />

                {/* 3. Quartos e camas */}
                <section>
                  <SectionHeader title={t("explore.roomsAndBeds") || "Quartos e camas"} />
                  <div className="space-y-1 divide-y divide-border/30">
                    <StepperCounter label={t("explore.bedrooms") || "Quartos"} value={tempFilters.bedrooms} onChange={(v) => setTempFilters(prev => ({ ...prev, bedrooms: v }))} />
                    <StepperCounter label={t("explore.beds") || "Camas"} value={tempFilters.beds} onChange={(v) => setTempFilters(prev => ({ ...prev, beds: v }))} />
                    <StepperCounter label={t("explore.bathrooms") || "WC"} value={tempFilters.bathrooms} onChange={(v) => setTempFilters(prev => ({ ...prev, bathrooms: v }))} />
                  </div>
                </section>

                <SectionDivider />

                <Accordion type="multiple" className="w-full">
                  <AccordionItem value="amenities" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{t('search.amenities')}</span>
                        {tempFilters.amenities.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.amenities.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        {AMENITIES.map((item) => (
                          <PillItem
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            selected={tempFilters.amenities.includes(item.id)}
                            onClick={() => toggleList("amenities", item.id)}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="features" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{t('search.features')}</span>
                        {tempFilters.features.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.features.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-2 gap-2">
                        {FEATURES.map((item) => (
                          <PillItem
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            selected={tempFilters.features.includes(item.id)}
                            onClick={() => toggleList("features", item.id)}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="safety" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{tAuto("auto.components_explore_propertyfilters.3", "Segurança")}</span>
                        {tempFilters.safety.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.safety.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-1 gap-2">
                        {SAFETY.map((item) => (
                          <PillItem
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            selected={tempFilters.safety.includes(item.id)}
                            onClick={() => toggleList("safety", item.id)}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="highlights" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">Em destaque</span>
                        {tempFilters.highlights.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.highlights.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="grid grid-cols-1 gap-2">
                        {HIGHLIGHTS.map((item) => (
                          <PillItem
                            key={item.id}
                            label={item.label}
                            icon={item.icon}
                            selected={tempFilters.highlights.includes(item.id)}
                            onClick={() => toggleList("highlights", item.id)}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>

                  <AccordionItem value="categories" className="border-0">
                    <AccordionTrigger className="py-2.5 hover:no-underline">
                      <div className="flex items-center justify-between w-full pr-2">
                        <span className="font-display text-base font-semibold text-foreground">{tAuto("auto.components_explore_propertyfilters.4", "Tipo de propriedade")}</span>
                        {tempFilters.propertyCategory.length > 0 && (
                          <span className="text-[11px] font-semibold text-primary bg-primary/10 border border-primary/20 px-2 py-0.5 rounded-full">
                            {tempFilters.propertyCategory.length}
                          </span>
                        )}
                      </div>
                    </AccordionTrigger>
                    <AccordionContent className="pt-2">
                      <div className="flex flex-wrap gap-2">
                        {PROPERTY_CATEGORIES.map((cat) => (
                          <PropertyTypePill
                            key={cat.id}
                            label={cat.label}
                            icon={cat.icon}
                            color={cat.color}
                            selected={tempFilters.propertyCategory.includes(cat.id)}
                            onClick={() => toggleList("propertyCategory", cat.id)}
                          />
                        ))}
                      </div>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>

                {/* Host language section removed */}

              </div>
            </ScrollArea>

            {/* Footer - Sticky */}
            <div className="px-7 py-5 border-t border-border/40 flex items-center justify-between bg-card flex-shrink-0">
              <button
                onClick={handleClearFilters}
                className="text-sm font-semibold text-foreground underline underline-offset-4 decoration-foreground/30 hover:decoration-foreground transition-colors"
              >
                {t('search.clearAll')}
              </button>
              <Button
                onClick={handleApplyFilters}
                size="lg"
                className="gradient-primary text-primary-foreground rounded-xl px-8 h-12 text-[15px] font-bold shadow-md hover:shadow-lg hover:opacity-95 transition-all active:scale-[0.98]"
              >
                {totalResults === 1 ? t('search.showResult') : t('search.showResults', { count: totalResults })}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      )}
      <div className="flex flex-wrap items-center gap-2">
          {/* Active Filters */}
          {activeFiltersCount > 0 && (
            <span className="text-xs font-medium text-muted-foreground">{t('search.filters')} ativos:</span>
          )}
          {activeFiltersCount > 0 && filters.accommodationType && (
            <Badge variant="secondary" className="gap-1.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
              {ACCOMMODATION_TYPES.find(t => t.value === filters.accommodationType)?.label}
              <X className="w-3 h-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => onFiltersChange({ ...filters, accommodationType: "" })} />
            </Badge>
          )}
          {filters.propertyCategory.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
              {PROPERTY_CATEGORIES.find(c => c.id === id)?.label}
              <X className="w-3 h-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => onFiltersChange({ ...filters, propertyCategory: filters.propertyCategory.filter(c => c !== id) })} />
            </Badge>
          ))}
          {filters.amenities.map((id) => (
            <Badge key={id} variant="secondary" className="gap-1.5 rounded-full text-xs bg-primary/10 text-primary border border-primary/20 hover:bg-primary/15">
              {AMENITIES.find(a => a.id === id)?.label}
              <X className="w-3 h-3 cursor-pointer hover:text-destructive transition-colors" onClick={() => onFiltersChange({ ...filters, amenities: filters.amenities.filter(a => a !== id) })} />
            </Badge>
          ))}
        </div>
    </div>
  );
};

export default PropertyFilters;
